import React, { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate, useBlocker } from 'react-router'
import { Box, Stack, Heading, Button, Alert, SimpleTabs, SimpleBreadcrumbs, UnsavedModal } from '@/components/lib'

import utils from '../../../utils/utils'
import api from '../../../api'
import useStore from '../../../store'
import useTitle from '../../../hooks/useTitle'

import Editor from './Editor'
import PollModeEditor from './Poll'
import ReminderEditor from './Reminders'
import SettingsEditor from './Settings'

import { POLL_FIELDS } from '../../Poll'

function NewStage () {
  const [stage, setStage] = useState()
  const [error, setError] = useState()
  const [errorComponent, setErrorComponent] = useState()
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [postSubmissionText, setPostSubmissionText] = useState('')
  const [showPostSubmissionEditor, setShowPostSubmissionEditor] = useState(false)
  const [isRecurring, setIsRecurring] = useState(false)
  const [recurringFrequency, setRecurringFrequency] = useState(0)
  const [recurringDuration, setRecurringDuration] = useState(0)
  const [adHoc, setAdHoc] = useState(false)
  const [isAnchored, setIsAnchored] = useState(false)
  const [anchorSchedule, setAnchorSchedule] = useState([])
  const [reminders, setReminders] = useState([])
  const [availableToSites, setAvailableToSites] = useState()
  const [enabled, setEnabled] = useState(true)
  const [enableAfter, setEnableAfter] = useState()
  const [enableAtTime, setEnableAtTime] = useState(null)
  const [exemptFromBehindStatus, setExemptFromBehindStatus] = useState(false)
  const [behindCutoffTime, setBehindCutoffTime] = useState(null)
  const [allowIncompleteSubmission, setAllowIncompleteSubmission] = useState(false)
  const [components, setComponents] = useState([])
  const [isPoll, setIsPoll] = useState(false)
  const [pollSettings, setPollSettings] = useState({})
  const [customLinks, setCustomLinks] = useState([])
  const [onSubmitRules, setOnSubmitRules] = useState([])
  const [unsaved, setUnsaved] = useState(false)
  const initializedRef = useRef(false) // Becomes true once the stage data has been loaded
  const navigate = useNavigate()
  const { stageId } = useParams()
  const { trial, updateTrial, addStage, team, dataTypes, sites, stages } = useStore()
  useTitle('Edit Stage', trial)
  const isEditing = !!stageId

  // Guard against marking the form as unsaved while the stage data is still being loaded.
  // initializedRef is set (via setTimeout) only after all setState calls from the load effect have settled.
  useEffect(() => {
    if (!initializedRef.current) return
    setUnsaved(true)
  }, [name, description, postSubmissionText, isRecurring, recurringFrequency, recurringDuration, adHoc, isAnchored, anchorSchedule, availableToSites, enabled, enableAfter, enableAtTime, exemptFromBehindStatus, behindCutoffTime, allowIncompleteSubmission, reminders, components, isPoll, pollSettings, customLinks, onSubmitRules])

  useEffect(() => {
    if (stageId) {
      api.stages.get(stageId, s => {
        addStage(s)
        setName(s.name || '')
        setDescription(s.description || '')
        setPostSubmissionText(s.postSubmissionText || '')
        setComponents(s.components || [])
        setIsRecurring(s.isRecurring || false)
        setRecurringFrequency(s.recurringFrequency || 0)
        setRecurringDuration(s.recurringDuration || 0)
        setAdHoc(s.adHoc || false)
        setIsAnchored(s.isAnchored || false)
        setAnchorSchedule(s.anchorSchedule || [])
        setReminders(s.reminders || [])
        setAvailableToSites(s.availableToSites || null)
        setEnabled(s.enabled || false)
        setEnableAfter(s.enableAfter || null)
        setEnableAtTime(s.enableAtTime || null)
        setExemptFromBehindStatus(s.exemptFromBehindStatus || false)
        setBehindCutoffTime(s.behindCutoffTime || null)
        setAllowIncompleteSubmission(s.allowIncompleteSubmission || false)
        setIsPoll(s.isPoll || false)
        setPollSettings(s.pollSettings || {})
        setCustomLinks(s.customLinks || [])
        setOnSubmitRules(s.onSubmitRules || [])
        setStage(s)
        setShowPostSubmissionEditor(true)
        setTimeout(() => { initializedRef.current = true })
      })
    }
    if (!stageId) {
      setShowPostSubmissionEditor(true)
      setTimeout(() => { initializedRef.current = true })
    }
  }, [stageId])

  const blocker = useBlocker(({ currentLocation, nextLocation }) =>
    unsaved &&
    currentLocation.pathname !== nextLocation.pathname
  )

  function create () {
    setError()
    setErrorComponent()
    const data = { name, description, postSubmissionText, isRecurring, recurringFrequency, recurringDuration, adHoc, isAnchored, anchorSchedule, availableToSites, enabled, enableAfter, enableAtTime, exemptFromBehindStatus, behindCutoffTime, allowIncompleteSubmission, reminders, components, isPoll, pollSettings, customLinks, onSubmitRules }
    data.components = data.components.map(c => {
      const component = { ...c }
      if (c.video?.url) component.video = { name: c.video.name, fileName: c.video.fileName, needsVideoProcess: c.video.needsVideoProcess } // Don't send signed URL back to server
      return component
    })
    if (isEditing) {
      api.stages.update(stageId, data, stage => {
        setUnsaved(false)
        addStage(stage)
        utils.toast('success', 'Stage saved')
      }, err => {
        setError(err.message)
        if (err.details?.errorComponent?.id) setErrorComponent(err.details.errorComponent.id)
      })
    } else {
      api.trials.createStage(trial._id, data, stage => {
        setUnsaved(false)
        addStage(stage)
        updateTrial(trial._id, { stageCount: (trial.stageCount ?? 0) + 1 })
        const navTarget = stages?.length === 0 ? 'manage' : 'stages'
        utils.toast('success', 'Stage created')
        setTimeout(() => navigate(`/trials/${trial._id}/${navTarget}`), 50) // Delay to ensure state is set
      }, err => setError(err.message))
    }
  }

  const warnings = []
  if (isPoll) {
    components?.forEach(c => {
      const dataType = dataTypes?.filter(d => d._id === c.dataType)[0]
      if (!Object.keys(POLL_FIELDS).includes(dataType?.type)) {
        if (c.type !== 'title' && c.type !== 'sectionDivider') {
          warnings.push(`The field "${(c.title || dataType?.name) ?? 'title or section'}" is not available in questionnaires.`)
        }
      }
    })
    if (isRecurring) {
      warnings.push('Enabling questionnaire mode for recurring stages will allow participants to submit to the same stage multiple times.')
    }
  }

  return (
    <Stack spacing={5} position='relative'>
      <UnsavedModal blocker={blocker} />
      <SimpleBreadcrumbs items={[
        { label: 'Stages', to: `/trials/${trial._id}/stages` },
        { label: stage?.name || 'New Stage' }
      ]}
      />
      <Heading as='h2' size='lg'>{isEditing ? `Edit stage ${stage?.name}` : 'Create a new stage'}</Heading>

      {warnings.length > 0 &&
        <Alert status='warning' title={`This stage has ${warnings.length} issue${warnings.length > 1 ? 's' : ''}`}>
          <Box>
            <ul>
              {warnings.map((w, i) => <li key={i}>{w}</li>)}
            </ul>
          </Box>
        </Alert>}

      <SimpleTabs
        defaultValue='Content' lazyMount
        tabs={[
          { title: 'Content', content: <Editor trial={trial} stage={stage} name={name} setName={setName} description={description} setDescription={setDescription} components={components} setComponents={setComponents} dataTypes={dataTypes} showPostSubmissionEditor={showPostSubmissionEditor} setShowPostSubmissionEditor={setShowPostSubmissionEditor} postSubmissionText={postSubmissionText} setPostSubmissionText={setPostSubmissionText} allowIncompleteSubmission={allowIncompleteSubmission} setAllowIncompleteSubmission={setAllowIncompleteSubmission} errorComponent={errorComponent} /> },
          { title: 'Reminders', content: <ReminderEditor reminders={reminders} setReminders={setReminders} /> },
          { title: 'Availability', content: <SettingsEditor trial={trial} stages={stages} stage={stage} isRecurring={isRecurring} setIsRecurring={setIsRecurring} recurringFrequency={recurringFrequency} setRecurringFrequency={setRecurringFrequency} recurringDuration={recurringDuration} setRecurringDuration={setRecurringDuration} adHoc={adHoc} setAdHoc={setAdHoc} isAnchored={isAnchored} setIsAnchored={setIsAnchored} anchorSchedule={anchorSchedule} setAnchorSchedule={setAnchorSchedule} availableToSites={availableToSites} setAvailableToSites={setAvailableToSites} enabled={enabled} setEnabled={setEnabled} enableAfter={enableAfter} sites={sites} setEnableAfter={setEnableAfter} enableAtTime={enableAtTime} setEnableAtTime={setEnableAtTime} exemptFromBehindStatus={exemptFromBehindStatus} setExemptFromBehindStatus={setExemptFromBehindStatus} behindCutoffTime={behindCutoffTime} setBehindCutoffTime={setBehindCutoffTime} isPoll={isPoll} /> },
          utils.hasFeature(team, 'pollMode') && { title: 'Questionnaire', content: <PollModeEditor trial={trial} stage={stage} isPoll={isPoll} setIsPoll={setIsPoll} pollSettings={pollSettings} setPollSettings={setPollSettings} customLinks={customLinks} setCustomLinks={setCustomLinks} onSubmitRules={onSubmitRules} setOnSubmitRules={setOnSubmitRules} /> }
        ]}
      />

      <Box m={10} position='sticky' style={{ bottom: 10 }} boxShadow='lg' p={3} rounded='md' bg='bg.accent'>
        <Stack direction='row' justifyContent='space-between' alignItems='center'>
          {error
            ? (
              <Alert status='error' variant='left-accent' title='There was a problem saving your stage'>
                {error}. Please fix these issues and try again.
              </Alert>)
            : <Box />}
          <Button colorPalette='primary' onClick={create}>{isEditing ? 'Save stage' : 'Create stage'}</Button>
        </Stack>
      </Box>
    </Stack>
  )
}

export default NewStage
